<script setup>
import {ref, computed, watchEffect} from "vue";
import closeImg from "@/assets/dialog/close.svg"
import {useScrollLock, onClickOutside} from "@vueuse/core";

defineOptions({
    name: 'BaseDialog'
});

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    size: {
        type: String,
        default: 'standard',
        validator: (value) => ['standard', 'widest', 'content'].includes(value)
    },
    closeOnClickOutside: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:modelValue', 'close']);

const dialogInnerWrapRef = ref(null);
const isLocked = useScrollLock(document.body);

onClickOutside(dialogInnerWrapRef, () => {
    props.closeOnClickOutside ? handleClose() : null
})

const heightWrapperClass = computed(() => {
    return `dialog-${props.size}`;
});

const fitContentStyle = computed(() => '');

const handleClose = () => {
    emit('update:modelValue', false);
    emit('close');
};

watchEffect(() => {
    isLocked.value = props.modelValue
})
</script>

<template>
    <Transition name="fade">
        <Teleport to="body">
            <div v-if="modelValue" class="dialog-outer_wrap">
                <div
                    ref="dialogInnerWrapRef"
                    class="dialog-inner_wrap"
                    :class="heightWrapperClass"
                    :style="fitContentStyle"
                    @click.stop
                >
                    <div class="dialog-head">
                        <div class="dialog-title">
                            <slot name="header"></slot>
                        </div>
                        <slot name="close-button">
                            <img :src="closeImg" class="dialog-close" @click="handleClose"/>
                        </slot>
                    </div>
                    <div class="dialog-content">
                        <slot name="default"></slot>
                    </div>
                </div>
            </div>
        </Teleport>
    </Transition>
</template>

<style lang="scss" scoped>
.dialog {
    &-outer_wrap {
        position: fixed;
        overflow: auto;
        width: 100vw;
        height: 100dvh;
        top: 0;
        left: 0;
        background-color: rgba(#08050cff, 0.85);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 11;
        padding: 20px;
    }

    &-inner_wrap {
        position: relative;
        width: max-content;
        min-width: 300px;
        min-height: 200px;
        max-height: calc(100vh - 40px);
        overflow-y: hidden;
        border-radius: 7px;
        background-color: white;
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 22px 28px;

        &.dialog-standard {
            min-height: unset;
        }
    }

    &-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: relative;
        background-color: white;
    }

    &-title {
        flex: 1;
        color: var(--text-color);
        text-align: center;
        font-size: 20px;
        font-style: normal;
        font-weight: 400;
        line-height: 30px;
        text-transform: uppercase;
    }

    &-close {
        position: absolute;
        top: -5px;
        right: 0;
        padding: 10px;
        border-radius: 3px;
        background-color: var(--block-color);
        cursor: pointer;
    }

    &-content {
        flex-grow: 1;
        overflow-y: auto;
    }

    &-widest {
        max-width: 750px;
        width: calc(100% - 40px);
    }

    &-standard {
        min-width: 420px;
        max-width: 560px;
        width: calc(100% - 40px);
    }

    @media screen and (max-width: 580px) {
        &-inner_wrap {
            max-width: calc(100% - 40px);
            min-height: 150px;
        }
        &-standard {
            min-width: fit-content;
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
